<template>
  <div v-intersect="handleIntersect" class="video-player">
    <AiMark v-if="aigc" class="video-player__ai-mark" theme="video" />

    <iframe
      v-if="isYoutubeEmbedUrl"
      class="video-player__iframe"
      :src="shouldLoad ? trueUrl : undefined"
      title="YouTube video player"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerpolicy="strict-origin-when-cross-origin"
      allowfullscreen
    >
    </iframe>
    <figure class="video-player__figure" v-else>
      <video
        id="videoPlay"
        ref="container"
        class="video-player__video video-js"
        :title="title"
        :data-src="props.url || props.autoUrl"
        :muted="muted"
        name="resourceVideo"
        :poster="computedPosterUrl"
        playsinline
        webkit-playsinline
        x5-video-player-type="h5"
        x5-video-player-fullscreen="false"
        x5-playsinline
        x-webkit-airplay="allow"
        weixin-playsinline="true"
        :loading="loading"
        :style="{
          background,
          objectFit: props.objectFit,
        }"
        @play="handlePlay"
        @ended="handleEnd"
        @timeupdate="handleTimeupdate"
        :fetchpriority="fetchpriority"
      />
      <figcaption>{{ title }}</figcaption>
    </figure>
  </div>
</template>
<script lang="ts" setup>
import { useBreakpoint } from '@rendora/design/index';
import AiMark from '~/components/ai-mark/index.vue';
import { transformImage } from '~/utils';
import VideoManager from './store';

// 仅在客户端导入 video.js，避免 SSR 构建错误
// 使用懒加载函数，确保只在真正需要时才导入
let videojs: any = null;
let en: any = null;
let zhCN: any = null;
let hlsQualitySelectorPluginLoaded = false;

// 懒加载 video.js 相关模块
const loadVideoJS = async () => {
  if (videojs) return; // 已经加载过

  if (import.meta.client) {
    const [videojsModule, enModule, zhCNModule] = await Promise.all([
      import('video.js'),
      import('video.js/dist/lang/en.json'),
      import('video.js/dist/lang/zh-CN.json'),
    ]);
    videojs = videojsModule.default;
    en = enModule.default;
    zhCN = zhCNModule.default;
    await import('video.js/dist/video-js.css');
  }
};

// 懒加载 HLS quality selector 插件
const loadHlsQualitySelector = async () => {
  if (hlsQualitySelectorPluginLoaded || !import.meta.client) return;
  // @ts-ignore - videojs-hls-quality-selector 没有类型定义
  await import('videojs-hls-quality-selector/src/plugin.js');
  hlsQualitySelectorPluginLoaded = true;
};

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

// 使用全局视频自动播放 Hook
const videoAutoplay = useGlobalVideoAutoplay();

const props = withDefaults(
  defineProps<{
    url?: string;
    autoUrl?: string;
    sources?: Array<{ src: string; type?: string }>; // 多个视频源支持
    poster?: string;
    id?: number;
    disablePictureInPicture?: boolean;
    muted?: boolean;
    autoplay?: boolean;
    loop?: boolean;
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    controls?: boolean;
    watermark?: string;
    aigc?: boolean;
    enableFullscreen?: boolean;
    fetchpriority?: 'auto' | 'high' | 'low';
    loading?: string;
    eager?: boolean; // 跳过 IntersectionObserver，直接在 onMounted 初始化（首屏 Banner 场景）
    type?: 'template' | 'class' | 'pieces'; // 模板|课程|成片
    startTime?: number; // 开始播放时间点
    posterTransform?: {
      height?: number;
      width?: number;
    };
    title?: string;
  }>(),
  {
    muted: true,
    autoplay: true,
    controls: true,
    loop: true,
    enableFullscreen: true,
    posterTransform: () => ({
      height: 720,
    }),
    objectFit: 'contain',
    fetchpriority: 'auto',
    eager: false,
  },
);

const emit = defineEmits(['ended', 'timeUpdate']);

const background = computed(() => {
  if (!props.watermark) {
    return;
  }
  const size = 12;
  const len = props.watermark.length;
  const rotate = 30;
  const width = len * size;
  const height = Math.floor(Math.tan((rotate / 180) * Math.PI) * width);
  const outerWidth = width + 20;
  const outerHeight = width + 0;
  const x = (outerWidth - width) / 2;
  const y = outerHeight - (outerHeight - height) / 2;
  const url = `"data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='120'><text x='${x}' y='${y}' font-size='${size}' fill='rgba(105, 98, 255, .5)' transform='rotate(-${rotate})' transform-origin='${x} ${y}'>${props.watermark}</text></svg>"`;
  return `url(${url}) repeat`;
});

const container = ref<HTMLVideoElement>();
const runtimeConfig = useRuntimeConfig();
const hasInitializedPlay = ref(false);
const shouldLoad = ref(false); // 懒加载控制
const isPlayerInitialized = ref(false);

let player: any = null;
let pendingAutoplayToken = 0;
let canPlayListener: (() => void) | null = null;

const tryPlayPlayer = () => {
  if (!props.autoplay || !player) return;
  if (videoAutoplay.needsUnlock.value && !videoAutoplay.isUnlocked.value) return;

  const playResult = player?.play?.();
  if (playResult && typeof playResult.catch === 'function') {
    playResult.catch(() => {});
  }
};

const tryPlayPendingSource = (token: number) => {
  if (token !== pendingAutoplayToken) {
    return;
  }

  pendingAutoplayToken = 0;
  tryPlayPlayer();
};

const clearPendingCanPlayListener = () => {
  if (canPlayListener && player?.off) {
    player.off('canplay', canPlayListener);
  }

  canPlayListener = null;
};

// 生成 MP4 fallback URL
const getMp4FallbackUrl = (url: string): string => {
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${url}.mp4`;
  }
  return `${url.substring(0, lastDotIndex)}.mp4`;
};

// 构建视频源列表
const getVideoSources = () => {
  // 在 SSR 时只使用 isMobile，避免访问 window 对象导致构建错误
  // isMobile 应该已经提供了正确的值，不需要额外检查 ontouchstart
  const mobile = isMobile.value;

  // 如果有 sources 配置，直接使用
  if (props.sources?.length) {
    const sources = [...props.sources];
    // 移动端确保 mp4 优先
    if (mobile && sources.length > 1) {
      const mp4Sources = sources.filter(s => s.type?.includes('mp4'));
      const otherSources = sources.filter(s => !s.type?.includes('mp4'));
      return [...mp4Sources, ...otherSources];
    }
    return sources;
  }

  // 单个 URL 处理
  const url = props.autoUrl || props.url;
  if (!url) return [];

  const ext = url.split('.').pop()?.toLowerCase();
  if (!ext) return [{ src: url }];

  const typeMap: Record<string, string> = {
    m3u8: 'application/x-mpegURL',
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
  };

  const originalSource = {
    src: url,
    type: typeMap[ext] || `video/${ext}`,
  };

  // 非 mp4 格式添加 fallback
  if (ext !== 'mp4') {
    const mp4Fallback = { src: getMp4FallbackUrl(url), type: 'video/mp4' };
    return mobile ? [mp4Fallback, originalSource] : [originalSource, mp4Fallback];
  }

  return [originalSource];
};

const initVideoPlayer = async () => {
  if (!container.value) return;

  // 确保 video.js 已加载
  await loadVideoJS();
  if (!videojs) return;

  const videoSources = getVideoSources();

  player = videojs(
    container.value,
    {
      sources: videoSources,
      language: runtimeConfig.public.area === 'US' ? 'en' : 'zh-CN',
      languages: {
        'zh-CN': { ...zhCN, Auto: '自动', Quality: '画质' },
        en: { ...en, Auto: 'Auto', Quality: 'Quality' },
      },
      objectFit: props.objectFit,
      controls: props.controls,
      playbackRates: [0.5, 0.75, 1, 1.5, 2],
      displayCurrentQuality: true,
      width: '100%',
      height: '100%',
      disablePictureInPicture: props.disablePictureInPicture,
      autoplay: props.autoplay,
      muted: props.muted,
      loop: props.loop,
      controlBar: {
        fullscreenToggle: false,
        pictureInPictureToggle: false,
      },
      fetchPriority: props.fetchpriority,
    },
    async () => {
      // 延迟加载插件，避免在 SSR 构建时被解析
      await loadHlsQualitySelector();
      (player as any).hlsQualitySelector();

      // 监听视频加载错误
      player?.on('error', () => {
        const error = player?.error();
        if (error) {
          console.warn('Video player error:', error.code, error.message);
          // Video.js 会自动尝试下一个 source
        }
      });

      if (props.autoplay && player) {
        // HLS 初始化时机不稳定，延迟执行确保正常
        setTimeout(() => {
          if (props.autoplay && player) {
            hasInitializedPlay.value = true;
            tryPlayPlayer();
          }
        });
      }
    },
  );
};

const computedPosterUrl = computed(() => {
  return props.poster ? transformImage(props.poster, props.posterTransform) : undefined;
});

const isYoutubeEmbedUrl = computed(() => {
  const url = props.url || props.autoUrl || '';
  return url.startsWith('https://www.youtube.com/embed/');
});

const trueUrl = computed(() => {
  if (isYoutubeEmbedUrl.value) return props.url;
  const url = props.autoUrl || props.url;
  return url?.endsWith('.m3u8') ? '' : url; // m3u8 由 video.js 处理
});

const destroy = () => {
  clearPendingCanPlayListener();
  if (document.pictureInPictureElement === container.value) {
    document.exitPictureInPicture();
  }
  player?.dispose();
  player = null;
  isPlayerInitialized.value = false;
};

watch([() => props.autoUrl, () => props.url, () => props.sources], () => {
  const videoSources = getVideoSources();
  if (!player) return;
  clearPendingCanPlayListener();

  if (videoSources.length > 0) {
    player.src(videoSources);
    player.poster(computedPosterUrl.value);
    player.playbackRate(1);
    if (props.autoplay && shouldLoad.value) {
      const autoplayToken = pendingAutoplayToken + 1;
      pendingAutoplayToken = autoplayToken;
      canPlayListener = () => {
        clearPendingCanPlayListener();
        tryPlayPendingSource(autoplayToken);
      };
      player.on?.('canplay', canPlayListener);
      nextTick(() => {
        if (player?.readyState?.() >= 3) {
          clearPendingCanPlayListener();
          tryPlayPendingSource(autoplayToken);
        }
      });
    }
  } else {
    pendingAutoplayToken = 0;
    clearPendingCanPlayListener();
    if (document.pictureInPictureElement === container.value) {
      document.exitPictureInPicture();
    } else {
      player.pause();
    }
    player.playbackRate(1);
  }
});

watch(
  () => props.startTime,
  startTime => {
    if (
      container.value &&
      startTime !== undefined &&
      Math.abs(container.value.currentTime - startTime) > 1
    ) {
      player?.currentTime(startTime);
    }
  },
);

watch(computedPosterUrl, newPosterUrl => {
  if (!player) return;
  player.poster(newPosterUrl);
});

watch(
  () => props.muted,
  newMuted => {
    player?.muted(newMuted);
  },
);

watch(
  () => videoAutoplay.isUnlocked.value,
  unlocked => {
    if (!unlocked || !props.autoplay || !shouldLoad.value) return;
    nextTick(() => {
      if (!isPlayerInitialized.value) {
        initPlayerIfNeeded();
        return;
      }
      tryPlayPlayer();
    });
  },
);

function handleEnd() {
  emit('ended');
}

function handleTimeupdate() {
  emit('timeUpdate', player?.currentTime());
}

function handlePlay() {
  if (container.value) {
    const currentPlayer = VideoManager.getCurrentVideoPlayer();
    if (currentPlayer && currentPlayer !== container.value) {
      currentPlayer.pause();
    }
    VideoManager.setCurrentVideoPlayer(container.value);
  }
}

const handleIntersect = (visible: boolean) => {
  if (visible) {
    shouldLoad.value = true;
    if (props.autoplay && player) {
      tryPlayPlayer();
    }
  } else if (hasInitializedPlay.value && player) {
    player.pause();
  }
};

useHead(() => {
  if (props.fetchpriority === 'high' && computedPosterUrl.value) {
    return {
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: computedPosterUrl.value,
          fetchpriority: 'high',
        },
      ],
    };
  }
  return {};
});

// 初始化播放器并注册
const initPlayerIfNeeded = async () => {
  if (isPlayerInitialized.value || isYoutubeEmbedUrl.value || !container.value) return;

  await initVideoPlayer();
  isPlayerInitialized.value = true;
  videoAutoplay.registerVideo(container.value);
};

// 监听 shouldLoad 变化，初始化播放器
watch(shouldLoad, newValue => {
  if (newValue) {
    nextTick(initPlayerIfNeeded);
  }
});

onMounted(() => {
  videoAutoplay.setupUnlockListeners();
  if (props.eager) {
    initPlayerIfNeeded();
  } else if (shouldLoad.value) {
    initPlayerIfNeeded();
  }
});

onBeforeUnmount(() => {
  container.value && videoAutoplay.unregisterVideo(container.value);
  destroy();
});

defineExpose({
  currentTime: () => {
    return player?.currentTime();
  },
  duration: () => {
    return player?.duration();
  },
  play: () => {
    player?.play();
  },
  pause: () => {
    player?.pause();
  },
});
</script>
<style lang="scss" scoped>
// 视频播放器容器
.video-player {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

// AI 标记
.video-player__ai-mark {
  position: absolute;
  right: 16px;
  top: 16px;
  z-index: 1;
}

// iframe 播放器（YouTube 等）
.video-player__iframe {
  width: 100%;
  height: 100%;
}

// figure 容器
.video-player__figure {
  width: 100%;
  height: 100%;
  margin: 0;
}

// 视频元素
.video-player__video {
  width: 100%;
  height: 100%;
  background-color: #2b2b2e;
  box-shadow: 0px 7px 36px 0px rgba(0, 0, 0, 0.1);
}

// video.js 样式覆盖
.video-player__video.video-js {
  background-color: transparent;
}

// 水印层
.video-player__video.video-js::after {
  position: absolute;
  display: block;
  height: 100%;
  width: 100%;
  pointer-events: none;
  content: ' ';
  background: v-bind(background);
}

// video.js 海报样式
.video-player__video.video-js :deep(.vjs-poster) {
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  width: 100% !important;
  height: 100% !important;
}

.video-player__video.video-js :deep(.vjs-poster img) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  object-position: center !important;
}
</style>
